'use client';
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";

import instance from "@/lib/axiosInstance";
import { useAuth } from "@/context/AuthContext";
import { EventListItem } from "@/components/EventListItem";

interface Event {
    id: number
    name: string
    slug: string
    description: string
    points: number
    image_url: string | null
    start_time: string
    slot_window_minutes: number
    slots_per_window: number
    tags: {
        name: string;
    }[]
    created_at: string
    updated_at: string
    is_registered: boolean
}

export default function Events() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [allEvents, setAllEvents] = useState<Event[]>([]); // Store all events
    const [activeFilter, setActiveFilter] = useState<'all' | 'upcoming' | 'past'>('all'); // Track active filter

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!user) return;

        const fetchData = async () => {
            try {
                const response = await instance.get('/community/events');
                setAllEvents(response.data.data.events || []);
            } catch (error) {
                console.error('Error fetching dispatch data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [user]);

    const handleEventFilter = (filter: 'all' | 'upcoming' | 'past') => {
        setActiveFilter(filter);
    };

    const filteredEvents = useMemo(() => {
        const now = Date.now();

        switch (activeFilter) {
            case 'upcoming':
                return allEvents.filter((event) => new Date(event.start_time).getTime() > now);
            case 'past':
                return allEvents.filter((event) => new Date(event.start_time).getTime() <= now);
            case 'all':
            default:
                return allEvents;
        }
    }, [allEvents, activeFilter]);

    return (
        <>
            <header className="flex opacity-0 sm:opacity-100 h-8 sm:h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
                <div className="flex items-center gap-2 px-4">
                    <SidebarTrigger className="-ml-1" />
                    <Separator orientation="vertical" className="mr-2 h-4" />
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem className="hidden md:block">
                                <BreadcrumbLink href="#">
                                    {t('dispatch.title')}
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
            </header>
            <div className="flex flex-1 flex-col gap-4 p-4 pt-0 w-full">
                <h1 className="text-xl font-semibold">
                    {t('navigation.contracts')}
                </h1>
                <div className="flex flex-col lg:flex-row w-full px-4 gap-4">
                    <div className="sticky top-8 flex flex-col w-full h-fit lg:w-1/5 border bg-muted/25 rounded-xl p-4">
                        <p
                            onClick={() => handleEventFilter('all')}
                            className={`h-fit w-full flex justify-between items-center text-sm cursor-pointer hover:bg-muted/50 transition duration-300 p-2 rounded-md ${activeFilter === 'all' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'
                                }`}
                        >
                            {t('dispatch.all_events')}
                            <span>{allEvents.length}</span>
                        </p>
                        {allEvents.some((event) => new Date(event.start_time).getTime() > Date.now()) && (
                            <p
                                onClick={() => handleEventFilter('upcoming')}
                                className={`h-fit w-full flex justify-between items-center text-sm cursor-pointer hover:bg-muted/50 transition duration-300 p-2 rounded-md ${activeFilter === 'upcoming' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'
                                    }`}
                            >
                                {t('dispatch.upcoming')}
                                <span>{allEvents.filter((event) => new Date(event.start_time).getTime() > Date.now()).length}</span>
                            </p>
                        )}
                        {allEvents.some((event) => new Date(event.start_time).getTime() < Date.now()) && (
                            <p
                                onClick={() => handleEventFilter('past')}
                                className={`h-fit w-full flex justify-between items-center text-sm cursor-pointer hover:bg-muted/50 transition duration-300 p-2 rounded-md ${activeFilter === 'past' ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'
                                    }`}
                            >
                                {t('dispatch.past')}
                                <span>{allEvents.filter((event) => new Date(event.start_time).getTime() < Date.now()).length}</span>
                            </p>
                        )}
                    </div>
                    <div className="grid grid-cols-1 w-full lg:w-4/5 gap-4">
                        {filteredEvents.length > 0 ? (
                            filteredEvents.map((event, index) => (
                                <EventListItem
                                    key={index}
                                    title={event.name}
                                    description={event.description}
                                    image_url={event.image_url || ''}
                                    tags={event.tags}
                                    xp={event.points}
                                    timestamp={new Date(event.start_time).getTime() / 1000}
                                    slug={event.slug}
                                    is_registered={event.is_registered}
                                />
                            ))
                        ) : (
                            <div className="col-span-full w-full p-8 text-center border border-dashed border-muted-foreground/30 rounded-xl bg-muted/10">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center">
                                        <svg className="w-8 h-8 text-muted-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-lg font-medium text-muted-foreground">
                                            {t('dispatch.no_events_found')}
                                        </p>
                                        <p className="text-sm text-muted-foreground/70">
                                            {t('dispatch.check_back_later', { type: t('dispatch.events').toLowerCase() })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}